import React, { useState, useEffect, useMemo } from "react";
import { Alert, CircularProgress, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CachedIcon from "@mui/icons-material/Cached";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { parseISO, isAfter } from "date-fns";

import { useGenerateFile, useGetFileInfo, useMemState } from "../../../hooks";
import { useGetEntity } from "../../../hooks";
import { LoadingOrContent, Modal } from "@/components";

/* eslint-disable react/prop-types */
const FileGeneratorUI = ({ scope, title, entity }) => {
  const [showReplicateBullhornDataModal, setShowReplicateBullhornDataModal] = useState(false);
  const [showGenerateFileModal, setShowGenerateFileModal] = useState(false);
  const [showCleanMemStateConfirmModal, setShowCleanMemStateConfirmModal] = useState(false);
  const [isReplicating, setIsReplicating] = useState(false);
  const [isGeneratingFile, setIsGeneratingFile] = useState(false);

  const { doRequest: replicateEntity, data: replicateEntityResponse } = useGetEntity(entity);
  const { doRequest: generateFile, data: generateFileResponse } = useGenerateFile(entity);
  const { data: fileInfo, isLoading: isGettingFileInfo } = useGetFileInfo(entity);

  /**
   * Note that the `useMemState` hook is receiving other request responses as a parameter.
   * It's used to initialize state based on the response from an endpoint.
   *
   * Ideally, this shouldn't be necessary, as any action in the service
   * should update the state appropriately.
   * However, in this case it's useful to display something closer to real-time.
   *
   * This wouldn't be necessary if we were using WebSockets instead of periodic requests.
   */
  const {
    state: { logs = [], generate_logs = [] } = {
      logs: [],
      generate_logs: []
    },
    isGeneratingCSV: isGeneratingCSVOnMemState,
    isReplicating: isReplicatingOnMemState,
    isCompleted,
    isGenerateFileCompleted,
    cleanMemState
  } = useMemState(replicateEntityResponse || generateFileResponse, scope);

  useEffect(() => setIsReplicating(!isCompleted && !!logs?.length), [isCompleted]);

  useEffect(
    () => setIsGeneratingFile(!isGenerateFileCompleted && !!generate_logs?.length),
    [isGenerateFileCompleted]
  );

  /**
   * Handle Start Replicate.
   */
  const handleConfirmReplicateBullhornData = () => {
    replicateEntity();
    setShowReplicateBullhornDataModal(false);
    setIsReplicating(true);
  };

  /**
   * Handle Start Generate CSV.
   */
  const handleConfirmGenerateFile = () => {
    generateFile();
    setShowGenerateFileModal(false);
    setIsGeneratingFile(true);
  };

  /**
   * Handle Clean Mem State.
   */
  const handleConfirmCleanMemState = () => {
    cleanMemState();
    setShowCleanMemStateConfirmModal(false);
  };

  // @TODO this could be a component.
  const getReplicateLog = (log) => {
    if (log.status === "completed") {
      return (
        <p>
          [{log.startDate}]: status <b>{log.status}</b>
        </p>
      );
    }
    return (
      <p>
        [{log.startDate}]: copying <b>{log.notes || 0}</b> of <b>{log.total || 0}</b>
      </p>
    );
  };

  const getGenerateFileLog = (log) => {
    if (log.status === "completed") {
      return (
        <p>
          [{log.startDate}]: status <b>{log.status}</b>
        </p>
      );
    }

    return (
      <p>
        [{log.startDate}]: message: <b>{log.message || "--"}</b>
      </p>
    );
  };

  const hasErrors = useMemo(
    () => [...logs, ...generate_logs]?.some((log) => log.status === "error"),
    [logs]
  );

  const replicateDataAlertState = useMemo(() => {
    if (hasErrors) {
      return "error";
    }

    return isCompleted ? "success" : "info";
  }, [isCompleted, hasErrors]);

  const generateFileAlertState = useMemo(() => {
    const hasErrors = generate_logs?.some((log) => log.status === "error");

    if (hasErrors) {
      return "error";
    }

    return isGenerateFileCompleted ? "success" : "info";
  }, [generate_logs, isGenerateFileCompleted]);

  const hasOldFile = useMemo(() => {
    const lastLog = logs[logs.length - 1];
    const lastGenerateLog = generate_logs[generate_logs.length - 1];

    const lastLogDate = parseISO(lastLog?.startDate);
    const lastGenerateLogDate = parseISO(lastGenerateLog?.startDate);

    return isAfter(lastLogDate, lastGenerateLogDate);
  }, [logs, generate_logs]);

  return (
    <>
      <h2>{title}</h2>
      {/*
       * Buttons
       */}
      <div style={{ display: "flex", gap: "4px", width: "100%", justifyContent: "flex-end" }}>
        <LoadingButton
          loading={isReplicating || isReplicatingOnMemState}
          variant="contained"
          onClick={() => setShowReplicateBullhornDataModal(true)}
          disabled={isGeneratingFile || isGeneratingCSVOnMemState}
        >
          Replicate Bullhorn Data
        </LoadingButton>

        <LoadingButton
          loading={isGeneratingFile || isGeneratingCSVOnMemState}
          variant="outlined"
          onClick={() => setShowGenerateFileModal(true)}
          disabled={isReplicating || isReplicatingOnMemState}
        >
          Generate CSV on GCS
        </LoadingButton>

        <Button onClick={() => setShowCleanMemStateConfirmModal(true)} variant="outlined">
          <CachedIcon />
        </Button>
      </div>

      <h3 style={{ display: "flex" }}>
        Replicate Bullhorn data logs:
        {(isReplicating || isReplicatingOnMemState) && <CircularProgress size={24} />}
      </h3>

      <Alert severity={replicateDataAlertState}>
        {logs?.map((log, index) => (
          <div key={index}>{getReplicateLog(log)}</div>
        ))}
      </Alert>

      <h3 style={{ display: "flex" }}>
        Generate CSV File logs:
        {(isGeneratingFile || isGeneratingCSVOnMemState) && <CircularProgress size={24} />}
      </h3>

      {hasOldFile && (
        <Alert severity="warning">The File Generated is older than the Replicated Data</Alert>
      )}

      <LoadingOrContent isLoading={isGettingFileInfo}>
        <Alert severity={generateFileAlertState}>
          {generate_logs?.map((log, index) => (
            <div key={index}>{getGenerateFileLog(log)}</div>
          ))}
        </Alert>

        <div style={{ width: "100%", textAlign: "right" }}>
          <LoadingButton
            href={fileInfo?.data?.url}
            variant="outlined"
            disabled={hasErrors || !fileInfo?.data?.url || isReplicating || isReplicatingOnMemState}
            startIcon={<CloudDownloadIcon />}
            loading={isGeneratingFile || isGeneratingCSVOnMemState}
          >
            {entity} CSV file
          </LoadingButton>
        </div>
      </LoadingOrContent>

      {/**
       * Modals
       *
       */}
      {showReplicateBullhornDataModal && (
        <Modal
          onClose={() => setShowReplicateBullhornDataModal(false)}
          onConfirm={handleConfirmReplicateBullhornData}
        >
          <h2 className="text-xl font-bold">Replicate Bullhorn Data</h2>

          <p className="mt-2">
            Do you want to confirm the start of the data copy process from Bullhorn?
          </p>

          <p>This process may take between 8 and 10 minutes.</p>
        </Modal>
      )}

      {showGenerateFileModal && (
        <Modal
          onClose={() => setShowGenerateFileModal(false)}
          onConfirm={handleConfirmGenerateFile}
        >
          <h2 className="text-xl font-bold">Generate CSV File</h2>

          <p className="mt-2">
            Do you want to confirm the start of the CSV file creation and saving process in GCS?
          </p>

          <p>This process may take around 5 minutes.</p>
        </Modal>
      )}

      {showCleanMemStateConfirmModal && (
        <Modal
          onClose={() => setShowCleanMemStateConfirmModal(false)}
          onConfirm={handleConfirmCleanMemState}
          containerStyles={{ width: 680 }}
        >
          <h2 className="text-xl font-bold">Clean Mem State</h2>

          <p className="mt-2">
            Are you sure you want to delete the status logs for the {entity} entity?
          </p>

          <p>
            This will delete the logs currently displayed on screen and reset the loading states.
            However, the database will remain up to date, and the latest file will stay correctly
            saved.
          </p>
        </Modal>
      )}
    </>
  );
};

export { FileGeneratorUI };
